/**
 * Cancel / Retry actions for a job, status-gated.
 *
 * - Cancel (in-flight: queued / queued-on-chassis / running) → POST
 *   /jobs/{id}/stop. A running job gets a confirm first (it may stop
 *   mid-change). Not-yet-started jobs go terminal immediately server-side.
 * - Retry (terminal-not-success: failed / partial / cancelled) → POST
 *   /jobs/{id}/retry, which re-queues a NEW job with the same params.
 *
 * Reused on the Jobs list + detail, the chassis Activity tab, and the Pending
 * results panel. CSRF is auto-attached by the api client.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api, apiErrorMessage } from "../../api/client";
import { Button } from "./Button";
import { useToast } from "./Toast";

const IN_FLIGHT = new Set(["queued", "queued-on-chassis", "running"]);
const RETRYABLE = new Set(["failed", "partial", "cancelled"]);

type NewJob = { id: string };

export function JobActions({
  job,
  className = "",
}: {
  job: { id: string; status: string };
  className?: string;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["v2", "jobs"] });
    qc.invalidateQueries({ queryKey: ["v2", "chassis"] });
    qc.invalidateQueries({ queryKey: ["v2", "tracker"] });
  };

  const cancel = useMutation({
    mutationFn: async () => {
      await api.post(`/jobs/${job.id}/stop`, {});
    },
    onSuccess: () => {
      invalidate();
      toast.push("Cancellation requested", "success");
    },
    onError: (err) => toast.push(apiErrorMessage(err, "Cancel failed"), "error"),
  });

  const retry = useMutation({
    mutationFn: async () => {
      const res = await api.post<NewJob>(`/jobs/${job.id}/retry`, {});
      return res.data;
    },
    onSuccess: (newJob) => {
      invalidate();
      toast.push(`Retry queued (${newJob.id.slice(0, 8)})`, "success");
      window.dispatchEvent(
        new CustomEvent("inline-job-tracker.add", {
          detail: { jobId: newJob.id, kind: "retry" },
        }),
      );
    },
    onError: (err) => toast.push(apiErrorMessage(err, "Retry failed"), "error"),
  });

  if (IN_FLIGHT.has(job.status)) {
    return (
      <Button
        variant="warning"
        size="xs"
        className={className}
        disabled={cancel.isPending}
        onClick={() => {
          if (
            job.status === "running" &&
            !window.confirm(
              "Cancel this running job? It may stop part-way through a change.",
            )
          ) {
            return;
          }
          cancel.mutate();
        }}
      >
        {cancel.isPending ? "…" : "Cancel"}
      </Button>
    );
  }

  if (RETRYABLE.has(job.status)) {
    return (
      <Button
        variant="primary"
        size="xs"
        className={className}
        disabled={retry.isPending}
        onClick={() => retry.mutate()}
        title="Re-queue this job with the same parameters"
      >
        {retry.isPending ? "…" : "Retry"}
      </Button>
    );
  }

  return null; // succeeded → nothing to do
}
